import { graphql } from 'react-relay';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { type TeamType, TEMPLATES_FOR_TEAM_TYPE } from '../utils/templateDetails.tsx';

import type {
	useTeamTypeUserQuery,
	useTeamTypeUserQueryVariables,
} from './__types__/useTeamTypeUserQuery.ts';
const teamTypeQuery = graphql`
	query useTeamTypeUserQuery($userId: ID!) {
		user(accountId: $userId) {
			__typename
			... on AtlassianAccountUser {
				extendedProfile {
					teamType
				}
			}
			id
		}
	}
`;

const TEAM_TYPES = Object.keys(TEMPLATES_FOR_TEAM_TYPE);

const sanitizeTeamType = (teamType: string | undefined | null): TeamType | undefined => {
	if (teamType && TEAM_TYPES.includes(teamType)) {
		return teamType as TeamType;
	}
	return undefined;
};

export const useTeamType = ({ skip }: { skip?: boolean }) => {
	const { userId } = useSessionData();
	const isLicensed = useIsLicensed();
	const { data, loading, error } = useQueryInterop<
		useTeamTypeUserQuery,
		useTeamTypeUserQueryVariables
	>(teamTypeQuery, {
		variables: { userId: userId ?? '' },
		skip: skip || !isLicensed || !userId,
		interopApolloAggClient: true,
	});
	if (loading) {
		return { teamType: undefined, loading: true };
	}

	if (error) {
		return { teamType: undefined, loading: false, error };
	}

	if (data?.user?.__typename === 'AtlassianAccountUser') {
		return { teamType: sanitizeTeamType(data.user.extendedProfile?.teamType), loading: false };
	}

	return { teamType: undefined, loading: false };
};
